import type { JSX } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDateInfo } from '@/features/experiences/types/experience.types';
import { useRefereeVerificationInfo } from '../hooks/useRefereeVerificationInfo';
import { RefereeConfirmForm } from '../components/RefereeConfirmForm';
import { RefereeDenyForm } from '../components/RefereeDenyForm';

type WizardStep = 'decision' | 'confirm' | 'deny';

export function RefereeDashboardPage(): JSX.Element {
  const { shareId } = useParams<{ shareId: string }>();
  const { data, isLoading, isError, error } = useRefereeVerificationInfo(shareId ?? '');
  const [step, setStep] = useState<WizardStep>('decision');

  if (isLoading) {
    return <div className="flex items-center justify-center p-6">Loading...</div>;
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error loading verification</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">{error?.message ?? 'Something went wrong.'}</p>
        </CardContent>
      </Card>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setStep('decision')}>
          &larr; Back
        </Button>
        <RefereeConfirmForm
          shareId={shareId ?? ''}
          questions={data.questions}
          initialRelationship={data.refereeRelationship}
          initialPhone={data.refereePhone}
          initialJobTitle={data.refereeJobTitle}
          initialAnswers={data.refereeAnswers}
        />
      </div>
    );
  }

  if (step === 'deny') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setStep('decision')}>
          &larr; Back
        </Button>
        <RefereeDenyForm shareId={shareId ?? ''} />
      </div>
    );
  }

  const startStr = formatDateInfo(data.experienceStartDate);
  const endStr = data.experienceEndDate ? formatDateInfo(data.experienceEndDate) : 'Present';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Welcome, {data.refereeName}!</h1>
      <p className="text-muted-foreground">
        You have been asked to verify {data.candidateName}&apos;s experience at{' '}
        {data.experienceCompanyName}.
      </p>
      <Card>
        <CardHeader>
          <CardTitle>Experience to Verify</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <p className="font-medium">{data.experienceCompanyName}</p>
          <p className="text-muted-foreground">{data.experienceJobTitle}</p>
          {startStr && (
            <p className="text-sm text-muted-foreground">
              {startStr} – {endStr}
            </p>
          )}
        </CardContent>
      </Card>
      <div className="flex gap-4">
        <Button onClick={() => setStep('confirm')}>Confirm</Button>
        <Button variant="outline" onClick={() => setStep('deny')}>
          Deny
        </Button>
      </div>
    </div>
  );
}
